import { ArrowLeft, ArrowRight, ImagePlus, RotateCw, Trash2, Upload } from 'lucide-react';
import { Button, IconButton, Input, Thumbnail, useToast } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import type { TypedClient } from '@platform/openapi';
import { useRef, type ClipboardEvent, type DragEvent, type ReactNode } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { acceptOf, imageFiles, type MediaView } from '../../media/media.ts';
import { useImageUploads, type PendingUpload } from '../../media/useImageUploads.ts';
import { MAX_ALT_LENGTH, type MediaEntry, type ProductFormValues } from '../product-form.ts';
import { EditorSectionCard } from './EditorSectionCard.tsx';
import styles from './MediaSection.module.css';

const PRODUCT_IMAGE = 'product_image';

const PERCENT = 100;

const ACTION_ICON_SIZE = 16;

const entryOf = (view: MediaView): MediaEntry | null =>
  view.urls === null
    ? null
    : { mediaId: view.id, alt: '', thumbnail: view.urls.w320, placeholder: view.placeholder };

const FilePicker = ({
  className,
  label,
  onFiles,
  children,
}: {
  className: string | undefined;
  label: string;
  onFiles: (files: Iterable<File>) => void;
  children: ReactNode;
}) => (
  <label className={className}>
    <input
      type="file"
      multiple
      accept={acceptOf(PRODUCT_IMAGE)}
      aria-label={label}
      className="visually-hidden"
      onChange={(event) => {
        onFiles(event.target.files ?? []);
        event.target.value = '';
      }}
    />
    {children}
  </label>
);

const PendingTile = ({
  upload,
  onRetry,
  onDismiss,
}: {
  upload: PendingUpload;
  onRetry: () => void;
  onDismiss: () => void;
}) => {
  const { t } = useTranslation();
  const failed = upload.phase === 'failed';
  return (
    <li className={[styles['tile'], failed ? styles['failed'] : ''].join(' ')} aria-busy={!failed}>
      <span className={styles['frame']}>
        <Thumbnail src={upload.preview} alt={upload.file.name} size="fill" />
      </span>
      <p className={styles['name']}>{upload.file.name}</p>
      {failed ? (
        <>
          <p role="alert" className={styles['error']}>
            {upload.error}
          </p>
          <div className={styles['pendingActions']}>
            <Button
              icon={<RotateCw size={ACTION_ICON_SIZE} aria-hidden="true" />}
              size="sm"
              variant="outline"
              onClick={onRetry}
            >
              {t('common.retry')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              {t('productEditor.mediaDismiss')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <progress
            className={styles['progress']}
            max={PERCENT}
            value={upload.phase === 'uploading' ? Math.round(upload.progress * PERCENT) : undefined}
            aria-label={t(`productEditor.mediaPhase_${upload.phase}`)}
          />
          <p className={styles['status']}>{t(`productEditor.mediaPhase_${upload.phase}`)}</p>
        </>
      )}
    </li>
  );
};

type MediaTileProps = {
  entry: MediaEntry;
  position: number;
  last: boolean;
  onMove: (to: number) => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDrop: (event: DragEvent<HTMLLIElement>) => void;
};

const MediaTile = ({ entry, position, last, onMove, onRemove, onDragStart, onDrop }: MediaTileProps) => {
  const { t } = useTranslation();
  const { register } = useFormContext<ProductFormValues>();
  const cover = position === 0;
  const name = entry.alt === '' ? t('productEditor.mediaImage', { position: position + 1 }) : entry.alt;
  return (
    <li
      className={[styles['tile'], cover ? styles['coverTile'] : ''].join(' ')}
      draggable
      onDragStart={onDragStart}
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDrop={onDrop}
    >
      <span className={styles['frame']}>
        <Thumbnail src={entry.thumbnail} placeholder={entry.placeholder} alt={name} size="fill" />
        {cover ? <span className={styles['cover']}>{t('productEditor.mediaCover')}</span> : null}
        <span className={styles['overlay']}>
          <IconButton
            size="sm"
            label={t('productEditor.mediaEarlier', { position: position + 1 })}
            icon={<ArrowLeft size={ACTION_ICON_SIZE} aria-hidden="true" className="flip-inline" />}
            disabled={cover}
            onClick={() => {
              onMove(position - 1);
            }}
          />
          <IconButton
            size="sm"
            label={t('productEditor.mediaLater', { position: position + 1 })}
            icon={<ArrowRight size={ACTION_ICON_SIZE} aria-hidden="true" className="flip-inline" />}
            disabled={last}
            onClick={() => {
              onMove(position + 1);
            }}
          />
          <IconButton
            size="sm"
            label={t('productEditor.mediaRemove', { position: position + 1 })}
            icon={<Trash2 size={ACTION_ICON_SIZE} aria-hidden="true" />}
            onClick={onRemove}
          />
        </span>
      </span>
      <Input
        label={t('productEditor.mediaAlt')}
        hideLabel
        placeholder={t('productEditor.mediaAlt')}
        maxLength={MAX_ALT_LENGTH}
        {...register(`media.${String(position)}.alt` as `media.${number}.alt`)}
      />
    </li>
  );
};

export const MediaSection = ({ api }: { api: TypedClient }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { control } = useFormContext<ProductFormValues>();
  const { fields, append, move, remove } = useFieldArray({ control, name: 'media', keyName: 'key' });
  const dragged = useRef<number | null>(null);
  const uploads = useImageUploads(api, PRODUCT_IMAGE, (view) => {
    const entry = entryOf(view);
    if (entry !== null) append(entry, { shouldFocus: false });
  });
  const empty = fields.length === 0 && uploads.pending.length === 0;

  const receive = (files: Iterable<File>) => {
    const { accepted, refused } = imageFiles(PRODUCT_IMAGE, files);
    for (const file of refused)
      toast.notify({ tone: 'danger', message: t('productEditor.mediaRefused', { name: file.name }) });
    if (accepted.length > 0) uploads.add(accepted);
  };

  const onDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    receive(event.dataTransfer.files);
  };

  const onPaste = (event: ClipboardEvent<HTMLElement>) => {
    if (event.clipboardData.files.length === 0) return;
    event.preventDefault();
    receive(event.clipboardData.files);
  };

  const dropOn = (position: number) => (event: DragEvent<HTMLLIElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (dragged.current !== null && dragged.current !== position) move(dragged.current, position);
    dragged.current = null;
  };

  return (
    <EditorSectionCard section="media">
      <div
        className={styles['stack']}
        onPaste={onPaste}
        onDragOver={(event) => {
          event.preventDefault();
        }}
        onDrop={onDrop}
      >
        {empty ? (
          <>
            <FilePicker className={styles['dropzone']} label={t('productEditor.mediaDrop')} onFiles={receive}>
              <Upload size={20} aria-hidden="true" />
              <strong>{t('productEditor.mediaDrop')}</strong>
              <span className={styles['help']}>{t('productEditor.mediaHelp')}</span>
            </FilePicker>
            <p className={styles['help']}>{t('productEditor.mediaEmpty')}</p>
          </>
        ) : (
          <>
            <ol className={styles['gallery']} aria-label={t('productEditor.mediaGallery')}>
              {fields.map((field, position) => (
                <MediaTile
                  key={field.key}
                  entry={field}
                  position={position}
                  last={position === fields.length - 1}
                  onMove={(to) => {
                    move(position, to);
                  }}
                  onRemove={() => {
                    remove(position);
                  }}
                  onDragStart={() => {
                    dragged.current = position;
                  }}
                  onDrop={dropOn(position)}
                />
              ))}
              {uploads.pending.map((upload) => (
                <PendingTile
                  key={upload.key}
                  upload={upload}
                  onRetry={() => {
                    uploads.retry(upload.key);
                  }}
                  onDismiss={() => {
                    uploads.dismiss(upload.key);
                  }}
                />
              ))}
              <li className={styles['addTile']}>
                <FilePicker
                  className={styles['addPicker']}
                  label={t('productEditor.mediaDrop')}
                  onFiles={receive}
                >
                  <ImagePlus size={24} aria-hidden="true" />
                  <strong>{t('productEditor.mediaAdd')}</strong>
                </FilePicker>
              </li>
            </ol>
            <p className={styles['help']}>{t('productEditor.mediaHelp')}</p>
          </>
        )}
      </div>
    </EditorSectionCard>
  );
};
